import { Fragment, type ReactNode } from "react";

import { parseNoteMarkdown, type NoteInline, type NoteMark } from "@/lib/noteMarkdown";

/**
 * The Planner webapp's note renderer: bold, italic, links and bullet / numbered
 * lists from a plain-text (Markdown subset) note. It reads the note with the
 * SAME parser the editor uses (lib/noteMarkdown), so the preview and the
 * editor never disagree about what is a link. No HTML is ever interpreted (no
 * dangerouslySetInnerHTML): markup stays text, and only http, https and mailto
 * become anchors. Bare URLs in plain text are linked too.
 */
const BARE_URL_RE = /((?:https?:\/\/|www\.)[^\s<*]+)/gi;
const LINK_CLASS = "text-link underline underline-offset-2 hover:text-foreground";

function safeHref(raw: string): string | null {
  const href = raw.startsWith("www.") ? `https://${raw}` : raw;
  try {
    const { protocol } = new URL(href);
    return protocol === "http:" || protocol === "https:" || protocol === "mailto:" ? href : null;
  } catch {
    return null;
  }
}

function splitTrailingPunctuation(raw: string): [string, string] {
  const match = raw.match(/^(.+?)([.,;:!?)]*)$/);
  return match ? [match[1], match[2]] : [raw, ""];
}

/** Plain text with its bare URLs turned into anchors. */
function linkifyBare(text: string, keyBase: string): ReactNode[] {
  return text.split(BARE_URL_RE).map((part, i) => {
    if (i % 2 === 0) return part;
    const [candidate, trailing] = splitTrailingPunctuation(part);
    const href = safeHref(candidate);
    if (!href) return part;
    return (
      <Fragment key={`${keyBase}-${i}`}>
        <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>{candidate}</a>
        {trailing}
      </Fragment>
    );
  });
}

function renderRun(node: NoteInline, key: string): ReactNode {
  if (node.type === "hardBreak") return "\n";
  const marks: NoteMark[] = node.marks ?? [];
  const link = marks.find((m): m is Extract<NoteMark, { type: "link" }> => m.type === "link");
  let content: ReactNode = link ? node.text : linkifyBare(node.text, key);
  if (marks.some((m) => m.type === "italic")) content = <em className="italic">{content}</em>;
  if (marks.some((m) => m.type === "bold")) content = <strong className="font-semibold">{content}</strong>;
  const href = link ? safeHref(link.attrs.href) : null;
  if (href) {
    content = (
      <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>{content}</a>
    );
  }
  return <Fragment key={key}>{content}</Fragment>;
}

function Inline({ nodes, base }: { nodes?: NoteInline[]; base: string }) {
  return <>{(nodes ?? []).map((n, i) => renderRun(n, `${base}-${i}`))}</>;
}

export function RichNoteText({ text, className = "" }: { text: string; className?: string }) {
  const doc = parseNoteMarkdown(text);
  return (
    <div className={`space-y-2 break-words ${className}`}>
      {doc.content.map((block, n) => {
        if (block.type === "paragraph") {
          if (!block.content?.length) return null;
          return (
            <p key={n} className="whitespace-pre-wrap">
              <Inline nodes={block.content} base={`p${n}`} />
            </p>
          );
        }
        const Tag = block.type === "bulletList" ? "ul" : "ol";
        return (
          <Tag key={n} className={Tag === "ul" ? "list-disc pl-5" : "list-decimal pl-5"}>
            {block.content.map((item, k) => (
              <li key={k}><Inline nodes={item.content[0]?.content} base={`l${n}-${k}`} /></li>
            ))}
          </Tag>
        );
      })}
    </div>
  );
}

export default RichNoteText;
